import { applyOverrides, type ColumnDef, type ResourceMap, type ResourceOverride } from '@pos/admin-ui';
import { RESOURCES } from '../generated/resources';

/**
 * Where the generated catalog screens need more than their generic labels (ADR 0030). `md.fields.price` is one label
 * for every table's `price`, so the item's own price is relabelled here as the base price.
 */
const unitColumns: readonly ColumnDef[] = [
  { key: 'uomId', type: 'reference', ref: 'catalog.uom', header: 'md.fields.uom_id' },
  { key: 'factorToBase', type: 'decimal', header: 'md.fields.factor_to_base' },
  { key: 'isBase', type: 'toggle', header: 'md.fields.is_base' },
  { key: 'isDefaultSale', type: 'toggle', header: 'md.fields.is_default_sale' },
  { key: 'sortOrder', type: 'number', header: 'md.fields.sort_order' },
  { key: 'isActive', type: 'toggle', header: 'md.fields.is_active' },
];

/** An item's unit is picked from that item's active selling units only (ADR 0030, ADR 0032). */
const itemUnits = {
  field: 'itemId',
  fieldLabel: 'md.fields.item_id',
  path: '/api/v1/catalog/items/{id}/units',
  valueField: 'uomId',
  activeField: 'isActive',
} as const;

export const CATALOG_OVERRIDES: Readonly<Record<string, ResourceOverride>> = {
  'catalog.item': {
    fields: {
      price: { label: 'catalog.item.basePrice', helper: 'catalog.item.basePriceHelp' },
      baseUomId: { helper: 'catalog.item.baseUnitHelp' },
    },
    columns: RESOURCES['catalog.item'].columns.map((c) =>
      c.key === 'price' ? { ...c, header: 'catalog.item.basePrice' as const } : c,
    ) as readonly ColumnDef[],
  },
  'catalog.item_uom': {
    columns: unitColumns,
    // The base row follows the item: its unit, factor and state are not edited here (the backend refuses).
    lock: { when: 'isBase', fields: ['uomId', 'factorToBase', 'isActive'] },
    fields: {
      factorToBase: { helper: 'catalog.unit.factorHelp' },
      isDefaultSale: { helper: 'catalog.unit.defaultHelp' },
      isBase: { hidden: true },
    },
  },
  // An option or deal choice adds one of the item's units; empty is its base unit (ADR 0032).
  'catalog.modifier': { fields: { uomId: { helper: 'catalog.unit.optionUnitHelp', limitTo: itemUnits } } },
  'catalog.deal_component_item': { fields: { uomId: { helper: 'catalog.unit.choiceUnitHelp', limitTo: itemUnits } } },
  // Standard costs and store recipes (ADR 0094): a brand row and store rows; a store recipe line in one of its ingredient's units.
  'catalog.item_cost': {
    fields: { storeId: { helper: 'catalog.cost.storeHelp' }, standardCost: { helper: 'catalog.cost.itemHelp' } },
  },
  'catalog.deal_cost': {
    fields: { storeId: { helper: 'catalog.cost.storeHelp' }, standardCost: { helper: 'catalog.cost.dealHelp' } },
  },
  'catalog.store_recipe': {
    fields: {
      storeId: { helper: 'catalog.cost.storeRecipeHelp' },
      uomId: { limitTo: { ...itemUnits, field: 'ingredientItemId', fieldLabel: 'md.fields.ingredient_item_id' } },
    },
  },
  // A deal can tax all its items at one VAT category (ADR 0033).
  'catalog.deal': { fields: { taxCategoryId: { helper: 'catalog.deal.taxHelp' } } },
  'menu.price_list_entry': { fields: { uomId: { limitTo: itemUnits } } },
  // A charge whose value the cashier types at the POS (FR-DSC-03): the value is the suggestion, the maximum the limit.
  'config.charge_type': {
    fields: {
      amountEntry: { helper: 'chargeTypes.amountEntryHelp' },
      maxValue: { helper: 'chargeTypes.maxValueHelp' },
    },
  },
};

export const HO_RESOURCES: ResourceMap = applyOverrides(RESOURCES as unknown as ResourceMap, CATALOG_OVERRIDES);
